import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';
import { getManyAdminPathways, getManyCollections, getManyFilesMetadata } from '../../../../../shared/actions';
import { deleteTeamFromList, putTeamAdmin } from '../../../../../shared/actions/Teams';
import FullPageLoadingIndicator from '../../../../../shared/components/FullPageLoadingIndicator';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import CompanyTeamsForm from './CompanyTeamsFormV2';
import TeamsTable from './CompanyTeamsTable';

class CompanyManageTeamsForm extends Component {
    static propTypes = {
        company: PropTypes.object,
        history: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        isInstructionalAdmin: PropTypes.bool,
        teams: PropTypes.array,
        teamsById: PropTypes.object,
        putTeamAdmin: PropTypes.func,
        deleteTeamFromList: PropTypes.func,
        match: PropTypes.object,
        sendEmailReminders: PropTypes.func,
        toggleShowUnsavedChangesPrompt: PropTypes.func,
        updateCurrentTeam: PropTypes.func,
        currentTeamName: PropTypes.string,
        getManyCollections: PropTypes.func,
        getManyAdminPathways: PropTypes.func,
        collectionsById: PropTypes.object,
        pathwaysById: PropTypes.object,
        featureFlagData: PropTypes.object,
        handleSuccessBannerTeamName: PropTypes.func,
        toggleTeamConfirmationModal: PropTypes.func,
        teamUnsavedConfimationModal: PropTypes.func,
        teamsUnsavedFlag: PropTypes.bool,
        setUnsavedChanges: PropTypes.func,
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            selectedTeamId: null,
            editing: false,
            mediaMetadata: [],
            isLoading: true
        };
    }

    componentDidMount() {
        const { isVerbAdmin, isAccountOwner, match, teamsById, updateCurrentTeam } = this.props;
        const teamId = match && match.params && match.params.teamId;
        if (teamId) {
            const teamName = teamsById[teamId]?.name;
            updateCurrentTeam(teamName);
        }
        return this._getTeamFeaturedContent()
            .then(() => {
                if (isVerbAdmin || isAccountOwner) {
                    return getManyFilesMetadata({ pathsContext: this.context.pathsWeb });
                }
                return [];
            })
            .then((mediaMetadata) =>
                this.setState({
                    mediaMetadata,
                    editing: !!teamId,
                    selectedTeamId: teamId && teamId !== 'new' ? teamId : null,
                    isLoading: false
                })
            );
    }

    componentDidUpdate(prevProps) {
        const { match, teamsById, updateCurrentTeam, currentTeamName, teams } = this.props;
        const teamId = match && match.params && match.params.teamId;
        const prevTeamId = prevProps.match && prevProps.match.params && prevProps.match.params.teamId;

        if (teamId !== prevTeamId) {
            if (teamId) {
                const teamName = teamsById[teamId]?.name;
                updateCurrentTeam(teamName);
            }
            return this.setState({
                editing: !!teamId,
                selectedTeamId: teamId !== 'new' ? teamId : null
            });
        }

        if (!teamId && currentTeamName) {
            updateCurrentTeam('');
        }

        if (prevProps?.teams?.length !== teams?.length) {
            return this._getTeamFeaturedContent();
        }

        return null;
    }

    componentWillUnmount() {
      const { showSuccessBannerTeam, handleSuccessBannerTeamName } = this.props;
      if (showSuccessBannerTeam) {
          handleSuccessBannerTeamName('', false);
      }
    }

    _getTeamFeaturedContent = () => {
        const { teams, getManyAdminPathways, getManyCollections } = this.props;
        if (teams.length) {
            const collectionIdList = [];
            const pathwayIdList = [];
            teams.forEach((team) => {
                if (team.currentAssignedPathway && team.currentAssignedPathway.pathwayId) {
                    pathwayIdList.push(team.currentAssignedPathway.pathwayId);
                }
                if (team.featuredCollectionInfo && team.featuredCollectionInfo.collectionId) {
                    collectionIdList.push(team.featuredCollectionInfo.collectionId);
                }
            });
            const promiseList = [];
            if (collectionIdList.length) {
                promiseList.push(getManyCollections({ _id: collectionIdList, pathsContext: this.context.pathsWeb }));
            }
            if (pathwayIdList.length) {
                promiseList.push(getManyAdminPathways({ _id: pathwayIdList, pathsContext: this.context.pathsWeb }));
            }
            return Promise.all(promiseList);
        }
        return Promise.resolve();
    };

    _goToTeamForm = (id, tabName) => {
        const { updateCurrentTeam, teamsById, handleSuccessBannerTeamName, showSuccessBannerTeam } = this.props;

        if (showSuccessBannerTeam) {
            handleSuccessBannerTeamName('', false);
        }

        let url = `/admin/editcompany/${this.props.match.params.id}/teams`;
        if (id) {
            const teamName = teamsById[id]?.name;
            updateCurrentTeam(teamName);
            url += `/${id}`;
        }
        if (id && tabName) {
            url += `/${tabName}`;
        }
        if (!id && !tabName) {
            url += `/new`;
        }
        return this.props.history.push(url);
    };

    returnToManageTable = () => {
        const { updateCurrentTeam, teamsUnsavedFlag, setUnsavedChanges } = this.props;
        
        // Check for unsaved changes before navigating
        if (teamsUnsavedFlag) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave?');
            if (!confirmed) {
                return; // Don't navigate if user cancels
            }
            // Clear the unsaved flag if user confirms
            setUnsavedChanges(false);

            // setTimeout to ensure state is updated before navigating
            setTimeout(() => {
                this.setState({
                    editing: false,
                    selectedTeamId: null
                });
                return this.props.history.push(`/admin/editcompany/${this.props.match.params.id}/teams`);
            }, 500);
        } else {
            this.setState({
                editing: false,
                selectedTeamId: null
            });
            return this.props.history.push(`/admin/editcompany/${this.props.match.params.id}/teams`);
        }
        
    };

    deleteTeam = (id, team) => {
        const { putTeamAdmin } = this.props;
        // eslint-disable-next-line no-alert
        const confirmation = window.confirm(`Are you sure you want to permanently delete team: ${team.name}?`);
        if (confirmation) {
            delete team._id;
            const body = { ...team, isDeleted: true };

            return putTeamAdmin({ teamId: id, body, pathsContext: this.context.pathsWeb })
                .then(() => this.props.deleteTeamFromList({ id }))
                .catch((err) => {
                    // eslint-disable-next-line no-alert
                    window.confirm(`Error deleting team: ${err}`);
                });
        }
        return null;
    };

    render() {
        const {
            teams,
            teamsById,
            history,
            company,
            isVerbAdmin,
            isInstructionalAdmin,
            sendEmailReminders,
            toggleShowUnsavedChangesPrompt,
            collectionsById,
            pathwaysById,
            match,
            featureFlagData,
            handleSuccessBannerTeamName,
            toggleTeamConfirmationModal,
            teamUnsavedConfimationModal,
        } = this.props;
        const { selectedTeamId, editing, mediaMetadata, isLoading } = this.state;
        const teamTab = match && match.params && match.params.teamTab;

        if (!isLoading) {
            if (editing) {
                if (selectedTeamId) {
                    const selectedTeam = teamsById[selectedTeamId];
                    return (
                        <div className={'company-teams-form-container'}>
                            <CompanyTeamsForm
                                isNew={false}
                                team={selectedTeam}
                                company={company}
                                mediaMetadata={mediaMetadata}
                                onExit={this.returnToManageTable}
                                isVerbAdmin={isVerbAdmin}
                                isInstructionalAdmin={isInstructionalAdmin}
                                sendEmailReminders={sendEmailReminders}
                                toggleShowUnsavedChangesPrompt={toggleShowUnsavedChangesPrompt}
                                teamTab={teamTab}
                                featureFlagData={featureFlagData}
                                handleSuccessBannerTeamName={handleSuccessBannerTeamName}
                                toggleTeamConfirmationModal={toggleTeamConfirmationModal}
                                teamUnsavedConfimationModal={teamUnsavedConfimationModal}
                                history={history}
                            />
                        </div>
                    );
                }
                return (
                    <div className={'company-teams-form-container'}>
                        <CompanyTeamsForm
                            isNew={true}
                            company={company}
                            onExit={this.returnToManageTable}
                            mediaMetadata={mediaMetadata}
                            isVerbAdmin={isVerbAdmin}
                            isInstructionalAdmin={isInstructionalAdmin}
                            onCreateSuccess={this._goToTeamForm}
                            toggleShowUnsavedChangesPrompt={toggleShowUnsavedChangesPrompt}
                            featureFlagData={featureFlagData}
                            handleSuccessBannerTeamName={handleSuccessBannerTeamName}
                            toggleTeamConfirmationModal={toggleTeamConfirmationModal}
                            history={history}
                        />
                    </div>
                );
            }
            return (
                <div className={'manage-teams-container'}>
                    <div>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                            fontAwesomeIcon={'fa-plus'}
                            onClick={() => this._goToTeamForm()}
                        >
                            NEW TEAM
                        </VerbButton>
                    </div>
                    <div className={'row'}>
                        <TeamsTable
                            isLoading={isLoading}
                            teams={teams}
                            history={history}
                            onEdit={this._goToTeamForm}
                            deleteTeam={this.deleteTeam}
                            collectionsById={collectionsById}
                            pathwaysById={pathwaysById}
                            isVerbAdmin={isVerbAdmin}
                        />
                    </div>
                </div>
            );
        }
        return <FullPageLoadingIndicator />;
    }
}

export const CompanyManageTeamsFormComponent = CompanyManageTeamsForm;

export const mapStateToProps = (state, ownProps) => {
    const { teams: { byId = {} } = {}, pathways, collections } = state;
    const teams = Object.values(byId).filter((team) => team.parentId === ownProps.company._id);

    return {
        teams,
        teamsById: byId,
        pathwaysById: pathways.byId,
        collectionsById: collections.byId
    };
};

const mapDispatchToProps = (dispatch) => ({
    putTeamAdmin: (params) => dispatch(putTeamAdmin(params)),
    deleteTeamFromList: (params) => dispatch(deleteTeamFromList(params)),
    getManyCollections: (params) => dispatch(getManyCollections(params)),
    getManyAdminPathways: (params) => dispatch(getManyAdminPathways(params))
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyManageTeamsForm);
